import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { ObjectTitleService } from '@fi-app/utilities/fi-services/object-title.service';
import { RoleService } from '../services/role.service';
import { propComparator } from '@fi-shared/utils/array-sort-comparators';
import { EditTabModel, EditTabsModel } from '@fi-app/utilities/edit-tabs/edit-tabs.model';
import { AppTextService } from '@fi-shared/app-text';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { StatisticService } from '@fi-shared/statistic/statistic.service';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { ModuleType } from '@fi-shared/types/module-type';
import { EditTabsComponent } from '@fi-app/utilities/edit-tabs/edit-tabs.component';
import { RoleModel } from '../role.model';
import { RoleEditAfterSaveDialogComponent } from './role-edit-after-save-dialog/role-edit-after-save-dialog.component';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { ApplicationSettingsModel } from '@fi-app/role/application-settings.model';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { RoleHelperService } from '../services/role-helper.service';
import { RolePageLayout } from '@fi-app/role/role-new-layout/role-new-layout.component';
import { CustomButtonModel } from '@fi-app/core/custom-button.model';
import { ILightWeightObject } from '@fi-shared/models';
import { ExtendSharingService } from '@fi-app/components/extend-sharing/extend-sharing.service';
import { NgModel } from '@angular/forms';
import { BehaviorSubject, Observable } from 'rxjs';
import { FiValidatingFormDirective } from '@fi-shared/fi-directives/fi-validating-form/fi-validating-form.directive';
import { FiContentValidatorTooltipComponent } from '@fi-shared/fi-tooltip/fi-content-validator-tooltip/fi-content-validator-tooltip.component';

@Component({
    selector: 'fi-role-edit',
    templateUrl: './role-edit.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./role-edit.component.scss'],
    host: { class: 'fi-role-edit' },
    standalone: false,
})
export class RoleEditComponent implements OnInit {
    EditTabs: EditTabsModel;
    @Input() role: RoleModel;
    @Input() allRoles: ILightWeightObject[];
    @Input() set selectedLayout(value: RolePageLayout) {
        this.setRolePageLayout(value);
    }
    @Output() afterSave = new EventEmitter();
    @Input() goToListAfterSave: boolean = true;
    @ViewChild(EditTabsComponent, { static: false }) editTabsComponent: EditTabsComponent;
    @ViewChild(FiValidatingFormDirective) roleForm: FiValidatingFormDirective;

    private isNewRole: boolean;

    private _rolePageLayout$ = new BehaviorSubject<RolePageLayout>(RolePageLayout.RoleModules);
    get rolePageLayout$(): Observable<RolePageLayout> {
        return this._rolePageLayout$.asObservable();
    }
    setRolePageLayout(value: RolePageLayout) {
        this._rolePageLayout$.next(value);
    }
    get rolePageLayoutValue(): RolePageLayout {
        return this._rolePageLayout$.value;
    }

    roleApplicationSettingGroups: {
        [key: string]: ApplicationSettingsModel[];
    };

    customButtons: CustomButtonModel[];

    indeterminate = false;

    constructor(
        private objectTitleService: ObjectTitleService,
        private roleService: RoleService,
        private roleHelperService: RoleHelperService,
        private appTextService: AppTextService,
        private fiLoadingService: LoadingService,
        private statisticService: StatisticService,
        private saveChangesWarningService: SaveChangesWarningService,
        private fiDialogsService: FiDialogsService,
        private stateService: FiStateService,
        private extendSharingService: ExtendSharingService,
    ) {}

    ngOnInit() {
        this.isNewRole = !this.role.Id;

        this.customButtons = this.role.CustomButtons.sort(propComparator('SortIndex'));

        this.setTabs();

        const getModelData = () => ({
            role: this.role,
        });

        // should wait for initializing ExtendedShareRoleCollection
        setTimeout(() => this.saveChangesWarningService.start(getModelData));
        this.statisticService.Visit(ModuleType.Core, ObjectTypes.Role, this.role.Id);

        this.roleApplicationSettingGroups = this.roleHelperService.getRoleApplicationSettingGroups(
            this.role.RoleApplicationSettings,
        );
    }

    private setTabs() {
        const title = this.objectTitleService.GetTitle(this.role.Id, this.role.Name, ObjectTypes.Role);
        this.EditTabs = new EditTabsModel(title);

        this.EditTabs.Tabs.push(
            new EditTabModel(
                this.appTextService.get('role.rights'),
                this.appTextService.get('role.rights'),
                '/Module/Core/Role/View/EditAccessRights.html',
            ),
        );
    }

    Save() {
        this.fiLoadingService.trackPromise(this.roleService.saveRole(this.role)).then((roleId: number) => {
            this.role.Id = roleId;
            this._afterSave();
        });
    }

    onValidateError(inputsWithErrors: NgModel[]) {
        if (
            inputsWithErrors.find((model: NgModel) => model.errors['roleNoName']) &&
            this.rolePageLayoutValue !== RolePageLayout.RoleModules
        ) {
            this.setRolePageLayout(RolePageLayout.RoleModules);
            setTimeout(() => this.triggerRoleNameErrorTooltipAppearance());
        }

        this.fiDialogsService.notify({
            messageText: this.appTextService.get('common.error_all_mandatory'),
            headerText: this.appTextService.get('common.warning'),
        });
    }

    private triggerRoleNameErrorTooltipAppearance() {
        const contentValidator: NgModel = this.roleForm.getDescendantNgModels().find((descendantNgModel: NgModel) => {
            return descendantNgModel.valueAccessor instanceof FiContentValidatorTooltipComponent;
        });
        contentValidator.control.markAsTouched();
        contentValidator.control.updateValueAndValidity();
    }

    private _afterSave() {
        this.saveChangesWarningService.stop();
        this.afterSave.emit(this.role);
        if (this.goToListAfterSave) {
            this.stateService.go(FiStateNames.Role.List);
            if (this.isNewRole) {
                setTimeout(() => {
                    this.fiDialogsService
                        .custom({
                            headerText: this.appTextService.get('role.new_role_saved'),
                            headerIconClass: 'BrandedColor fal fa-bookmark',
                            yesButtonText: this.appTextService.get('common.go_to_setup'),
                            noButtonText: this.appTextService.get('common.skip'),
                            contentComponentRef: RoleEditAfterSaveDialogComponent,
                            contentComponentData: {
                                hasAccessToModulesForSharing: this.extendSharingService.hasAccessToExtendSharing(),
                            },
                            config: {
                                panelClass: 'fi-rounded-dialog-panel',
                            },
                        })
                        .onYesButtonClick((result) => {
                            if (result.setupRoleRelations) {
                                this.stateService.go(FiStateNames.Role.RoleRelations, {
                                    Id: this.role.Id,
                                    shareExistingContent: result.shareExistingContent,
                                });
                            } else {
                                if (result.shareExistingContent) {
                                    this.extendSharingService.openExtendedSharing(this.role.Id, ObjectTypes.Role);
                                } else {
                                    this.stateService.go(FiStateNames.Role.List);
                                }
                            }
                        });
                });
            }
        }
    }

    back() {
        this.roleHelperService.setRoleDataChanged(false);
        window.history.back();
    }
}
